// SPDX-License-Identifier: AGPL-3.0-or-later

import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {Button} from '@app/features/ui/button/Button';
import type {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useId} from 'react';
import {inventoryBadgePresentation} from './BadgeInventoryBadge';
import type {InventoryBadge} from './BadgeInventoryState';
import {type EarnableBadgeKey, earnableBadges} from './BadgeTiers';
import styles from './EarnableBadges.module.css';

const TITLE = msg({
	message: 'Badges you can get',
	comment: 'Title of the list of built-in badges the user does not own yet but can still get.',
});
const HOW: Record<EarnableBadgeKey, ReturnType<typeof msg>> = {
	premium: msg({
		message: 'Worn while your Plus subscription is active.',
		comment: 'How to get the Plus crown badge, shown to a user without it.',
	}),
	premium_package: msg({
		message: 'Buy a Plus package once and keep this badge after Plus ends. A longer package gives a higher tier.',
		comment: 'How to get the early supporter badge (1, 3 or 12 month Plus package), shown to a user without it.',
	}),
	premium_sequence: msg({
		message: 'Visionary members wear their lifetime member number. Places are limited.',
		comment: 'How to get the numbered Visionary badge, shown only while Visionary places remain.',
	}),
	booster: msg({
		message: 'Boost a community from its boost panel. The badge grows the longer you boost without a break.',
		comment: 'How to get the server booster badge; it is bought inside a community, so there is no button.',
	}),
};
const ACTIONS = {
	premium: msg({message: 'Get Plus', comment: 'Button opening the Plus page from the badges the user can get.'}),
	premium_package: msg({
		message: 'See packages',
		comment: 'Button opening the Plus page, where the Plus packages are sold, from the badges the user can get.',
	}),
	premium_sequence: msg({
		message: 'See Visionary',
		comment: 'Button opening the Plus page, where Visionary is sold, from the badges the user can get.',
	}),
} satisfies Record<Exclude<EarnableBadgeKey, 'booster'>, unknown>;

// The owned-inventory shape only to reuse its badge names.
const SAMPLES: Record<EarnableBadgeKey, InventoryBadge> = {
	premium: {source: 'premium', key: 'premium', visible: false},
	premium_package: {source: 'premium', key: 'premium_package', visible: false},
	premium_sequence: {source: 'premium', key: 'premium_sequence', visible: false},
	booster: {source: 'boost', key: 'booster', visible: false, since: new Date(0).toISOString()},
};

export const EarnableBadges = observer(({user, ownedKeys}: {user: User; ownedKeys: ReadonlySet<string>}) => {
	const {i18n} = useLingui();
	const titleId = useId();
	useEffect(() => {
		void ShopCatalog.load();
	}, []);
	if (ShopCatalog.status !== 'ready') return null;
	const offers = earnableBadges({ownedKeys, user, shop: ShopCatalog, premiumVisible: shouldShowPremiumFeatures()});
	if (offers.length === 0) return null;
	return (
		<section
			className={styles.section}
			aria-labelledby={titleId}
			data-flx="user.my-profile-tab.earnable-badges.section"
		>
			<h3 id={titleId} className={styles.title} data-flx="user.my-profile-tab.earnable-badges.title">
				{i18n._(TITLE)}
			</h3>
			<ul className={styles.list} data-flx="user.my-profile-tab.earnable-badges.list">
				{offers.map((offer) => (
					<li
						key={offer.key}
						className={styles.item}
						data-earnable={offer.key}
						data-flx="user.my-profile-tab.earnable-badges.item"
					>
						<span className={styles.art} aria-hidden="true" data-flx="user.my-profile-tab.earnable-badges.art">
							{offer.iconUrl ? (
								<img src={offer.iconUrl} alt="" draggable={false} data-flx="user.my-profile-tab.earnable-badges.img" />
							) : (
								'#'
							)}
						</span>
						<span className={styles.text} data-flx="user.my-profile-tab.earnable-badges.text">
							<span className={styles.name} data-flx="user.my-profile-tab.earnable-badges.name">
								{inventoryBadgePresentation(SAMPLES[offer.key], user, i18n).name}
							</span>
							<span className={styles.how} data-flx="user.my-profile-tab.earnable-badges.how">
								{i18n._(HOW[offer.key])}
							</span>
						</span>
						{offer.action && offer.key !== 'booster' && (
							<span className={styles.action} data-flx="user.my-profile-tab.earnable-badges.action">
								<Button
									small
									variant="secondary"
									fitContent
									onClick={() => PremiumModalCommands.open()}
									data-flx="user.my-profile-tab.earnable-badges.button.open"
								>
									{i18n._(ACTIONS[offer.key])}
								</Button>
							</span>
						)}
					</li>
				))}
			</ul>
		</section>
	);
});
